import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Save, Plus, Trash2, Star, CheckCircle, Send, Mail, Clock, X, Copy, FileSignature, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const UnderwritingNotesForm = ({ caseId, notes = [], agent, onUpdate }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [newNote, setNewNote] = useState({
    note_date: new Date().toISOString().slice(0, 10),
    carrier: '', policy_number: '', status: 'submitted', notes: '',
    requirements_outstanding: '', aps_requested: false, paramedical_requested: false, lab_requested: false
  });

  const add = async () => {
    await fetch(`${API_URL}/api/compliance/cases/${caseId}/underwriting?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newNote)
    });
    setShowAdd(false);
    setNewNote({ note_date: new Date().toISOString().slice(0, 10), carrier: '', policy_number: '', status: 'submitted', notes: '', requirements_outstanding: '', aps_requested: false, paramedical_requested: false, lab_requested: false });
    onUpdate();
  };

  return (
    <Card data-testid="underwriting-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Underwriting Notes</CardTitle>
        <Button onClick={() => setShowAdd(!showAdd)} size="sm" className="bg-[#0077B6]" data-testid="add-uw-btn">
          <Plus className="h-4 w-4 mr-1" />Add Note
        </Button>
      </CardHeader>
      <CardContent>
        {showAdd && (
          <div className="border rounded-lg p-4 mb-4 bg-blue-50 grid md:grid-cols-2 gap-3">
            <input type="date" value={newNote.note_date} onChange={(e) => setNewNote({ ...newNote, note_date: e.target.value })} className="px-2 py-2 border rounded" data-testid="uw-date" />
            <input value={newNote.carrier} onChange={(e) => setNewNote({ ...newNote, carrier: e.target.value })} placeholder="Carrier" className="px-2 py-2 border rounded" data-testid="uw-carrier" />
            <input value={newNote.policy_number} onChange={(e) => setNewNote({ ...newNote, policy_number: e.target.value })} placeholder="Policy Number" className="px-2 py-2 border rounded" data-testid="uw-policy" />
            <select value={newNote.status} onChange={(e) => setNewNote({ ...newNote, status: e.target.value })} className="px-2 py-2 border rounded" data-testid="uw-status">
              <option value="submitted">Submitted</option>
              <option value="in_review">In Review</option>
              <option value="requirements_pending">Requirements Pending</option>
              <option value="rated">Rated</option>
              <option value="approved">Approved</option>
              <option value="declined">Declined</option>
            </select>
            <input value={newNote.requirements_outstanding} onChange={(e) => setNewNote({ ...newNote, requirements_outstanding: e.target.value })} placeholder="Outstanding Requirements" className="px-2 py-2 border rounded md:col-span-2" data-testid="uw-requirements" />
            <textarea rows="2" value={newNote.notes} onChange={(e) => setNewNote({ ...newNote, notes: e.target.value })} placeholder="Notes" className="px-2 py-2 border rounded md:col-span-2" data-testid="uw-notes" />
            <div className="flex gap-4 md:col-span-2">
              <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={newNote.aps_requested} onChange={(e) => setNewNote({ ...newNote, aps_requested: e.target.checked })} data-testid="uw-aps" /> APS Requested</label>
              <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={newNote.paramedical_requested} onChange={(e) => setNewNote({ ...newNote, paramedical_requested: e.target.checked })} data-testid="uw-paramedical" /> Paramedical</label>
              <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={newNote.lab_requested} onChange={(e) => setNewNote({ ...newNote, lab_requested: e.target.checked })} data-testid="uw-lab" /> Lab</label>
            </div>
            <div className="md:col-span-2 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button onClick={add} className="bg-[#0077B6]" data-testid="save-uw-btn">Save Note</Button>
            </div>
          </div>
        )}
        {notes.length === 0 ? (
          <p className="text-gray-500 text-center py-6">No underwriting notes yet.</p>
        ) : (
          <div className="space-y-2">
            {notes.map(n => (
              <div key={n.id} className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium">{n.note_date} — {n.carrier}</span>
                  <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">{n.status?.replace(/_/g, ' ')}</span>
                </div>
                {n.notes && <p className="text-sm text-gray-700">{n.notes}</p>}
                {n.requirements_outstanding && <p className="text-sm text-yellow-700 mt-1"><strong>Outstanding:</strong> {n.requirements_outstanding}</p>}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// =============================================================
// Policy Delivery Form
// =============================================================
